import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getRace } from '../api';
import {
  clearStoredRaceCode,
  getFavouriteDriver,
  getStoredRaceCode,
  setFavouriteDriver,
  setStoredRaceCode,
} from '../user';
import {
  clearDriverLiveAccessToken,
  getDriverLiveAccessToken,
  getDriverLiveDrivers,
  requestDriverLiveAccess,
} from '../driverLiveApi';
import useDriverLive from '../useDriverLive';
import useRacePlayback from '../useRacePlayback';
import PlaybackMode from '../components/PlaybackMode';
import { chooseInitialFavourite } from '../driverLiveUi';
import { AccessGate, AvailabilityCard, DriverList, DriverPanel } from '../components/DriverLiveView';

function errorAvailability(err) {
  if (!err) return null;
  if (['RACE_NOT_FOUND', 'DRIVER_LIVE_CANCELLED', 'DRIVER_LIVE_ENDED', 'DRIVER_LIVE_NOT_OPEN', 'DRIVER_LIVE_UNCONFIGURED', 'DRIVER_LIVE_NO_SESSION', 'DRIVER_LIVE_RATE_LIMITED'].includes(err.code)) {
    return { code: err.code, detail: err.message };
  }
  if (err.code === 'REQUEST_TIMEOUT') return { code: 'REQUEST_TIMEOUT', detail: err.message };
  if (!err.status || err.status >= 500) return { code: 'TEMPORARILY_UNAVAILABLE', detail: err.message };
  return null;
}

export default function DriverLivePage() {
  const { id } = useParams();
  const playback = useRacePlayback(id, { followPub: true, subscribe: false });
  const [race, setRace] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [driversKey, setDriversKey] = useState(null);
  const [selected, setSelected] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [gateError, setGateError] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [metadataError, setMetadataError] = useState(null);
  const [busy, setBusy] = useState(true);
  const [favourite, setFavourite] = useState(getFavouriteDriver);
  const playbackKey = `${id}:${playback.mode}:${playback.settings?.version ?? 0}:${playback.settings?.delayMs ?? 0}`;
  const visibleDrivers = useMemo(() => driversKey === playbackKey ? drivers : [], [driversKey, playbackKey, drivers]);
  const generationRef = useRef(0);
  const autoFavouriteRaceRef = useRef(null);

  const authenticate = useCallback(async (code, silent = false, generation = generationRef.current) => {
    setBusy(true);
    if (!silent) setGateError(null);
    setAvailability(null);
    try {
      const result = await requestDriverLiveAccess(id, code);
      if (generation !== generationRef.current) return null;
      const normalized = String(code).trim().toUpperCase();
      setStoredRaceCode(id, normalized);
      setAccessToken(result.accessToken);
      return result.accessToken;
    } catch (err) {
      if (generation !== generationRef.current) return null;
      const unavailable = errorAvailability(err);
      if (unavailable) setAvailability(unavailable);
      else if (err.code === 'DRIVER_LIVE_CODE_INVALID' || !silent) setGateError(err.message);
      if (err.code === 'DRIVER_LIVE_CODE_INVALID') clearStoredRaceCode(id);
      setAccessToken(null);
      return null;
    } finally {
      if (generation === generationRef.current) setBusy(false);
    }
  }, [id]);

  useEffect(() => {
    const generation = ++generationRef.current;
    autoFavouriteRaceRef.current = null;
    setRace(null);
    setDrivers([]);
    setDriversKey(null);
    setSelected(null);
    setGateError(null);
    setAvailability(null);
    setMetadataError(null);
    setBusy(true);
    setAccessToken(null);

    getRace(id).then(r => { if (generation === generationRef.current) setRace(r); }).catch(() => {});
    const existing = getDriverLiveAccessToken(id);
    if (existing) {
      setAccessToken(existing);
      setBusy(false);
    } else {
      const stored = getStoredRaceCode(id);
      if (stored) authenticate(stored, true, generation);
      else setBusy(false);
    }
    return () => { if (generation === generationRef.current) generationRef.current += 1; };
  }, [id, authenticate]);

  useEffect(() => {
    if (!accessToken || playback.mode === 'pending') return undefined;
    let disposed = false;
    let pending = false;
    let controller = null;
    const load = async () => {
      if (disposed || pending) return;
      pending = true;
      controller = new AbortController();
      try {
        const result = await getDriverLiveDrivers(id, controller.signal, playback.mode);
        if (!disposed) {
          const nextDrivers = result.drivers || [];
          setDrivers(nextDrivers);
          setDriversKey(playbackKey);
          setGateError(null);
          setAvailability(null);
          setMetadataError(nextDrivers.length
            ? null
            : 'Driver list is temporarily empty. Retrying while live data remains available.');
        }
      } catch (err) {
        if (disposed || controller.signal.aborted) return;
        const terminalCodes = new Set([
          'RACE_NOT_FOUND',
          'DRIVER_LIVE_CANCELLED',
          'DRIVER_LIVE_ENDED',
          'DRIVER_LIVE_NOT_OPEN',
          'DRIVER_LIVE_UNCONFIGURED',
          'DRIVER_LIVE_NO_SESSION',
          'DRIVER_LIVE_ACCESS_EXPIRED',
          'DRIVER_LIVE_ACCESS_REVOKED',
        ]);
        if ([401, 403, 410].includes(err.status) || terminalCodes.has(err.code)) {
          const unavailable = errorAvailability(err);
          if (unavailable) setAvailability(unavailable);
          clearDriverLiveAccessToken(id);
          setAccessToken(null);
        } else {
          // Metadata refresh is advisory once access has been established.
          // Keep the current list, selection and working telemetry stream while
          // retrying transient timeouts/5xx failures on the existing bounded loop.
          setMetadataError(err.message || 'Driver list refresh delayed. Retrying…');
        }
      } finally { pending = false; }
    };
    load();
    const timer = setInterval(load, 15000);
    return () => { disposed = true; clearInterval(timer); controller?.abort(); };
  }, [id, accessToken, playback.mode, playbackKey]);

  useEffect(() => {
    if (!visibleDrivers.length || autoFavouriteRaceRef.current === id) return;
    autoFavouriteRaceRef.current = id;
    const initial = chooseInitialFavourite(visibleDrivers, favourite);
    if (initial != null) setSelected(initial);
  }, [id, visibleDrivers, favourite]);

  const handleExpired = useCallback(async code => {
    clearDriverLiveAccessToken(id);
    setAccessToken(null);
    const terminal = errorAvailability({ code, message: '' });
    if (terminal && code !== 'DRIVER_LIVE_ACCESS_EXPIRED') {
      setAvailability(terminal);
      return;
    }
    const stored = getStoredRaceCode(id);
    if (stored) await authenticate(stored, true);
  }, [id, authenticate]);

  const live = useDriverLive(id, selected, accessToken, handleExpired, playback.mode, playback.settings?.version);
  const toggleFavourite = acronym => {
    const next = favourite === acronym ? '' : acronym;
    setFavourite(next);
    setFavouriteDriver(next);
  };

  const retry = () => {
    const stored = getStoredRaceCode(id);
    if (stored) authenticate(stored, true);
    else setAvailability(null);
  };

  if (availability && !accessToken) return <AvailabilityCard {...availability} busy={busy} onRetry={retry} />;
  if (!accessToken) {
    if (busy && getStoredRaceCode(id)) return <p style={{ color: '#666', padding: '40px 0', textAlign: 'center' }}>Opening Driver Live…</p>;
    return <AccessGate race={race} error={gateError} busy={busy} onSubmit={code => authenticate(code, false)} />;
  }

  return (
    <div className="driver-live">
      <Link to="/live" className="dl-back">← Back to Live</Link>
      <div className="dl-page-heading">
        <div><span className="dl-eyebrow">Your personal pit wall</span><h1>DRIVER <span>LIVE</span></h1></div>
        <p>{race?.name || 'Live race telemetry'}</p>
      </div>
      <PlaybackMode mode={playback.mode} onMode={playback.chooseMode} settings={playback.settings}
        status={playback.mode === 'pub' ? (live.snapshot ? 'ready' : 'warming') : 'live'} />
      {metadataError && (
        <div role="status" className="dl-notice">
          {metadataError}
        </div>
      )}
      {playback.mode === 'pending' && <div role="status" className="dl-notice">TV sync setting unavailable — waiting for server. Live data is withheld.</div>}
      {playback.mode !== 'pending' && (selected == null
        ? <DriverList drivers={visibleDrivers} favourite={favourite} onChoose={setSelected} onFavourite={toggleFavourite} />
        : <DriverPanel data={live.snapshot} displayStatus={live.displayStatus} now={live.now} error={live.error} favourite={favourite} onBack={() => setSelected(null)} onFavourite={toggleFavourite} drivers={visibleDrivers} selected={selected} onChoose={setSelected} synchronised={playback.mode === 'pub'} />)}
    </div>
  );
}
